import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import SaveOutlined from '@ant-design/icons/SaveOutlined'
import { pdf } from '@react-pdf/renderer'
import { Alert, Button, message, Progress, Space, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useContextBridge } from 'its-fine'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import {
  RugReportContext,
  useRugReportOps,
} from 'pages/reporting/reports/rug/RugReportProvider'
import { PDFDocument } from 'pages/reporting/shared/pdfGeneration/PDFDocument'
import { PDFGenerationSection } from 'pages/reporting/shared/types'
import React, { useCallback, useRef, useState } from 'react'

const { Title, Text } = Typography

const RenderReact: React.FC = () => {
  const contextValue = useRugReportOps()
  const {
    pdfState,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    isSubmitting,
    report,
    metadataEntryId,
    inspections,
    forceRefreshPdf,
  } = contextValue

  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState<string>('')
  // Seems a bit silly, but we need to maintain section pages outside of PDF Document
  // So that we can render the table of contents a second time with the correct section pages
  const initialSectionPagesRef = useRef<Record<string, number>>({})

  const { rolodexUploadFile } = useRolodexEdit()

  const Bridge = useContextBridge()

  const handleOnPageRender = useCallback(
    ({ id, pageNumber }: { id: string; pageNumber: number }) => {
      setProgress((prev) => Math.min(prev + 8, 90))
      setCurrentStep(`Generating ${id} page...`)
      initialSectionPagesRef.current = {
        ...initialSectionPagesRef.current,
        [id]: pageNumber,
      }
    },
    [setProgress, setCurrentStep]
  )

  const generatePDF = useCallback(async () => {
    setProgress(0)
    setCurrentStep('Preparing PDF document...')
    setProgress(20)

    // Yup, this looks a bit stupid, but what're ya gonna do?
    // The Table of Contents PDF is rendered along the other components, but we want it to know the final page numbers
    // Of the other PDf components. The simplest way to do this is to simply render twice!
    // First render populates our reference of initial section pages, and the second render creates the PDF with that page data.
    // PDF rendering should always be identical across these two renders.
    const initialPdfInstance = (
      <RugReportContext.Provider value={contextValue}>
        <Bridge>
          <PDFDocument
            report={report}
            onPageRender={handleOnPageRender}
            initialSectionPages={initialSectionPagesRef.current}
          />
        </Bridge>
      </RugReportContext.Provider>
    )

    setCurrentStep('Initializing PDF...')
    await pdf(initialPdfInstance).toBlob()
    setProgress(20)

    const pdfInstance = (
      <RugReportContext.Provider value={contextValue}>
        <Bridge>
          <PDFDocument
            report={report}
            onPageRender={handleOnPageRender}
            initialSectionPages={initialSectionPagesRef.current}
          />
        </Bridge>
      </RugReportContext.Provider>
    )

    setCurrentStep('Finalizing PDF...')
    const blob = await pdf(pdfInstance).toBlob()
    setProgress(100)
    return blob
  }, [contextValue, report, Bridge, handleOnPageRender])

  // Generate and save PDF draft
  const handleGeneratePDF = useCallback(async () => {
    try {
      setIsGenerating(true)
      const pdfBlob = await generatePDF()

      const d = new Date()
      const pad = (n: number) => String(n).padStart(2, '0')
      const timestamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`
      if (inspections && inspections.length > 0) {
        const inspectionIds = inspections.map((i) => i.id).join('-')
        const path = `reportPDFDrafts/rug-report-${inspectionIds}-${metadataEntryId}-draft-${timestamp}.pdf`
        await rolodexUploadFile(metadataEntryId, pdfBlob, path)
      }

      message.success('PDF generated successfully')
      await forceRefreshPdf()
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      message.error('Failed to generate PDF')
    } finally {
      setIsGenerating(false)
      setProgress(0)
      setCurrentStep('')
    }
  }, [
    generatePDF,
    inspections,
    metadataEntryId,
    rolodexUploadFile,
    forceRefreshPdf,
  ])

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <Title level={2}>Generate PDF</Title>
      <Text>
        Generate and download the final RUG inspection report PDF. You can
        create a draft version for review or publish the final version.
      </Text>

      <div style={{ marginTop: '32px' }}>
        <Space orientation='vertical' size='large' style={{ width: '100%' }}>
          <div>
            <Title level={4}>Draft PDF</Title>
            <Text type='secondary'>
              Generate a draft PDF for review and internal use.
            </Text>
            <div style={{ marginTop: '16px' }}>
              <Space
                orientation='vertical'
                size='small'
                style={{ width: '100%' }}>
                {isGenerating ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Space
                      orientation='vertical'
                      align='center'
                      style={{ width: '100%' }}>
                      <LoadingOutlined style={{ fontSize: 24 }} />
                      <Progress percent={progress} status='active' />
                      <Text>{currentStep}</Text>
                    </Space>
                  </div>
                ) : (
                  <Space wrap>
                    <Button
                      type='primary'
                      icon={<SaveOutlined />}
                      onClick={handleGeneratePDF}
                      loading={isGenerating}
                      disabled={isGenerating}>
                      Generate PDF
                    </Button>
                    {pdfState.latestDraftTimestamp && (
                      <>
                        <Button
                          icon={<FileTextOutlined />}
                          onClick={handleDownloadLatestDraft}
                          loading={isSubmitting}>
                          Download Latest Draft
                        </Button>
                        <Text type='secondary'>
                          Last generated:{' '}
                          {new Date(
                            pdfState.latestDraftTimestamp
                          ).toLocaleString()}
                        </Text>
                      </>
                    )}
                  </Space>
                )}
              </Space>
            </div>
          </div>

          <div>
            <Title level={4}>Published PDF</Title>
            <Text type='secondary'>
              Publish and download the final official RUG inspection report PDF.
            </Text>
            <div style={{ marginTop: '16px' }}>
              <Space orientation='vertical' size='small'>
                {!pdfState.hasPublishedPdf ? (
                  <Button
                    type='primary'
                    danger
                    icon={<DownloadOutlined />}
                    onClick={publishLatestDraftPDF}
                    loading={isSubmitting}>
                    Publish Final PDF
                  </Button>
                ) : (
                  <Space>
                    <Button
                      type='primary'
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadPublishedPdf}>
                      Download Published PDF
                    </Button>
                    <Button
                      type='default'
                      onClick={publishLatestDraftPDF}
                      loading={isSubmitting}>
                      Update Published PDF
                    </Button>
                  </Space>
                )}
                {pdfState.hasPublishedPdf && (
                  <Alert
                    title='PDF Published'
                    description='The official RUG inspection report PDF has been published and is available for download.'
                    type='success'
                    showIcon
                  />
                )}
              </Space>
            </div>
          </div>
        </Space>
      </div>
    </div>
  )
}

export const PDFGeneration: PDFGenerationSection = {
  id: RugReportSectionId.PDF_GENERATION,
  title: 'Generate PDF',
  description: 'Generate and download final report',
  RenderReact,
  required: false,
}
